import { useState, useEffect, useRef } from "react";
import { Calendar, Clock, CheckCircle, DollarSign, BarChart2, Users, AlertTriangle, Mail, RefreshCw, X, CreditCard, MessageSquare, Smartphone } from "lucide-react";
import { listEvents } from '../../Fetch/fetchEvent';
import { getTasks } from '../../Fetch/fetchTasks';
import { getListUsersExternalDashboard } from '../../Fetch/fetchUser';
import { getListPaymentsByBuildingId } from '../../Fetch/fetchPayment';
import { getExpensesExternalDashboard } from '../../Fetch/fetchExpenses';
import { getMessages } from '../../Fetch/fetchMessage';
import { getBuildingDataExternal } from './SendDashboardUrl';
import { fetchBuildings } from '../../Fetch/fetchBuilding';
import { getLatestBankExternal } from '../../Fetch/fetchBank';

// Auto-scroll hook for tables with continuous scrolling - OPTIMIZED
const useAutoScroll = (contentLength, itemsVisible = 6, scrollSpeed = 100) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const intervalRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const isPausedRef = useRef(false);

  useEffect(() => {
    // Cleanup function to clear all timers
    const cleanup = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }
    };

    if (contentLength <= itemsVisible) {
      setScrollPosition(0);
      setIsScrolling(false);
      cleanup();
      return;
    }

    setIsScrolling(true);
    const rowHeight = 70;
    const maxScroll = (contentLength - itemsVisible) * rowHeight;
    const scrollStep = 1;
    const pauseDuration = 3000; // Increased to 3 seconds

    intervalRef.current = setInterval(() => {
      if (isPausedRef.current) return;

      setScrollPosition(prevPosition => {
        const nextPosition = prevPosition + scrollStep;

        if (nextPosition >= maxScroll && !pauseTimeoutRef.current) {
          isPausedRef.current = true;
          pauseTimeoutRef.current = setTimeout(() => {
            setScrollPosition(0);
            isPausedRef.current = false;
            pauseTimeoutRef.current = null;
          }, pauseDuration);
          return maxScroll;
        }

        if (nextPosition < maxScroll) {
          return nextPosition;
        }

        return prevPosition;
      });
    }, scrollSpeed);

    return cleanup;
  }, [contentLength, itemsVisible, scrollSpeed]);

  // Optimized transform update using requestAnimationFrame
  useEffect(() => {
    if (!containerRef.current || contentLength <= itemsVisible) return;

    const scrollContainer = containerRef.current.querySelector('[data-scroll-container]');
    if (scrollContainer) {
      requestAnimationFrame(() => {
        scrollContainer.style.transform = `translateY(-${scrollPosition}px)`;
      });
    }
  }, [scrollPosition, contentLength, itemsVisible]);

  return { scrollPosition, containerRef, isScrolling };
};

// Modal Component for Unpaid Details
const UnpaidDetailsModal = ({ user, isOpen, onClose, onSendReminder }) => {
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);

  if (!isOpen || !user) return null;

  const handleSendReminder = async () => {
    if (!user.email) {
      setReminderStatus('error');
      setTimeout(() => setReminderStatus(null), 3000);
      return;
    }

    setIsSendingReminder(true);
    setReminderStatus(null);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '1rem'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.8rem', fontWeight: '700' }}>
            פירוט חובות - {user.name}
          </h3>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem'
          }}>
            <X size={24} />
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ margin: '0.75rem 0', fontSize: '1.425rem' }}><strong>דירה:</strong> {user.apartmentNumber}</p>
            <p style={{ margin: '0.75rem 0', fontSize: '1.425rem' }}><strong>טלפון:</strong> {user.phone || 'לא זמין'}</p>
            <p style={{ margin: '0.75rem 0', fontSize: '1.425rem' }}><strong>אימייל:</strong> {user.email || 'לא זמין'}</p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0.75rem 0', fontSize: '1.55rem' }}>חודשים שלא שולמו ({user.monthsUnpaidCount} חודשים):</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.75rem',
              marginTop: '0.75rem'
            }}>
              {user.unpaidMonths && user.unpaidMonths.length > 0 ? (
                user.unpaidMonths.map((month, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#fef2f2',
                    borderRadius: '0.375rem',
                    fontSize: '1.3rem'
                  }}>
                    <Calendar size={18} style={{ marginLeft: '0.5rem' }} />
                    <span>{month}</span>
                  </div>
                ))
              ) : (
                <p>אין נתונים על חודשים לא שולמו</p>
              )}
            </div>
          </div>

          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.55rem' }}>
              <span style={{ fontWeight: '600' }}>סכום חוב משוער:</span>
              <span style={{ fontWeight: '700', color: '#dc2626' }}>
                ₪{(user.monthsUnpaidCount * 500).toLocaleString()}
              </span>
            </div>
          </div>

          {reminderStatus === 'success' && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#dcfce7',
              color: '#15803d',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              fontSize: '1.425rem'
            }}>
              ✅ תזכורת התשלום נשלחה בהצלחה!
            </div>
          )}
          {reminderStatus === 'error' && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              fontSize: '1.425rem'
            }}>
              ❌ שגיאה בשליחת התזכורת. {!user.email ? 'כתובת אימייל לא זמינה.' : 'נסה שוב מאוחר יותר.'}
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          borderTop: '2px solid #e5e7eb',
          paddingTop: '1.5rem'
        }}>
          <button onClick={onClose} style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1.425rem'
          }}>
            סגור
          </button>
          <button
            onClick={handleSendReminder}
            disabled={isSendingReminder || !user.email}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: user.email ? '#3b82f6' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: user.email ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1.425rem'
            }}
          >
            {isSendingReminder ? (
              <>
                <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }} />
                שולח...
              </>
            ) : (
              <>
                <Mail size={18} />
                שלח תזכורת
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function BuildingCommitteeDashboard() {
  // State for building data from URL instead of localStorage
  const [building, setBuilding] = useState(null);
  const [buildingId, setBuildingId] = useState(null);
  const [buildingCreateDate, setBuildingCreateDate] = useState(null);
  const [loadingBuilding, setLoadingBuilding] = useState(true);
  const [buildingError, setBuildingError] = useState(null);

  // State to track if page is visible (for pause/resume timers)
  const [isPageVisible, setIsPageVisible] = useState(true);

  // State for financial data from database
  const [paymentsData, setPaymentsData] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [currentMonthIncome, setCurrentMonthIncome] = useState(0);
  const [currentMonthExpenses, setCurrentMonthExpenses] = useState(0);
  const [currentBankBalance, setCurrentBankBalance] = useState(null);
  const [loadingFinances, setLoadingFinances] = useState(true);
  const [financesError, setFinancesError] = useState(null);

  // State for tasks data from database
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [tasksError, setTasksError] = useState(null);

  // State for messages data from database
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [messagesError, setMessagesError] = useState(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // State for last meeting data from database
  const [lastMeeting, setLastMeeting] = useState(null);
  const [loadingMeeting, setLoadingMeeting] = useState(true);
  const [meetingError, setMeetingError] = useState(null);

  // State for next meeting data from database
  const [nextMeeting, setNextMeeting] = useState(null);
  const [loadingNextMeeting, setLoadingNextMeeting] = useState(true);
  const [nextMeetingError, setNextMeetingError] = useState(null);

  // State for unpaid users functionality
  const [users, setUsers] = useState([]);
  const [unpaidUsers, setUnpaidUsers] = useState([]);
  const [loadingUnpaidUsers, setLoadingUnpaidUsers] = useState(true);
  const [unpaidUsersError, setUnpaidUsersError] = useState(null);

  // Modal state for unpaid details
  const [selectedUnpaidUser, setSelectedUnpaidUser] = useState(null);
  const [isUnpaidModalOpen, setIsUnpaidModalOpen] = useState(false);

  // Reminder notification state
  const [reminderNotification, setReminderNotification] = useState(null);

  // Get current time
  const [currentTime, setCurrentTime] = useState(new Date());

  // Auto-scroll hooks for tables - continuous scrolling
  const unpaidUsersScroll = useAutoScroll(unpaidUsers.length, 6, 30); // Slower continuous scroll
  const tasksScroll = useAutoScroll(tasks.length, 6, 40); // Slightly faster continuous scroll

  // Auto-cycle messages for TV display - OPTIMIZED
  useEffect(() => {
    if (messages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % messages.length);
    }, 10000); // Changed from 8 to 10 seconds to reduce updates

    return () => clearInterval(interval);
  }, [messages.length]);

  // Function to calculate current month's income from payments
  const calculateCurrentMonthIncome = (payments) => {
    if (!payments || !Array.isArray(payments)) return 0;

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const currentMonthPayments = payments.filter(payment => {
      const paymentDate = new Date(payment.Date || payment.date);
      return paymentDate.getMonth() === currentMonth &&
        paymentDate.getFullYear() === currentYear;
    });

    return currentMonthPayments.reduce((total, payment) => {
      return total + (payment.Amount || payment.amount || 0);
    }, 0);
  };

  // Function to calculate current month's expenses
  const calculateCurrentMonthExpenses = (expenses) => {
    if (!expenses || !Array.isArray(expenses)) return 0;

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const currentMonthExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.Date || expense.date);
      return expenseDate.getMonth() === currentMonth &&
        expenseDate.getFullYear() === currentYear;
    });

    return currentMonthExpenses.reduce((total, expense) => {
      return total + (expense.Amount || expense.amount || 0);
    }, 0);
  };

  useEffect(() => {
  }, [unpaidUsers]);

  // Browser keep-alive mechanism for TV displays + Visibility API
  useEffect(() => {
    // Keep-alive: Prevent TV browser from sleeping by subtle DOM manipulation
    const keepAliveInterval = setInterval(() => {
      // Tiny opacity change to keep browser active (invisible to user)
      document.body.style.opacity = document.body.style.opacity === '1' ? '0.9999' : '1';
    }, 30000); // Every 30 seconds

    // Visibility API: Detect when page becomes hidden/visible
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);

      if (!document.hidden) {
        // Page became visible again - refresh data
        console.log('📺 Dashboard became visible - refreshing data');
        refreshAllData();
      } else {
        console.log('📺 Dashboard hidden - timers will continue but data refresh paused');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clock update timer - only update when visible
    const timer = setInterval(() => {
      if (!document.hidden) {
        setCurrentTime(new Date());
      }
    }, 1000);

    return () => {
      clearInterval(keepAliveInterval);
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);


  // FIXED calculateUnpaidUsers function - using EXACT same logic as Dashboard.jsx calculateUnpaidApartments
  // This version will produce identical results to your Dashboard.jsx

  const calculateUnpaidUsers = (users, payments, buildingCreateDate = null) => {

    if (!users || !Array.isArray(users)) {
      return [];
    }

    if (!payments || !Array.isArray(payments)) {
      payments = [];
    }

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    // EXACT same building start date calculation as Dashboard.jsx
    let paymentStartDate;

    if (buildingCreateDate && buildingCreateDate !== 'לא זמין' && buildingCreateDate !== null) {
      const testDate = safeParseDate(buildingCreateDate);

      if (testDate && testDate <= currentDate) {
        paymentStartDate = new Date(testDate.getFullYear(), testDate.getMonth(), 1);
      } else {
        paymentStartDate = null;
      }
    } else {
      paymentStartDate = null;
    }

    if (!paymentStartDate) {
      const startOfYear = new Date(currentYear, 0, 1);
      const twelveMonthsAgo = new Date(currentYear, currentMonth - 12, 1);
      paymentStartDate = startOfYear > twelveMonthsAgo ? startOfYear : twelveMonthsAgo;
    }

    // EXACT same months calculation as Dashboard.jsx
    const monthsToCheck = [];
    let checkDate = new Date(paymentStartDate.getFullYear(), paymentStartDate.getMonth(), 1);
    const endDate = new Date(currentYear, currentMonth - 1, 1);


    let monthCount = 0;
    while (checkDate <= endDate && monthCount < 50) {
      const monthData = {
        month: checkDate.getMonth() + 1,
        year: checkDate.getFullYear(),
        monthName: checkDate.toLocaleDateString('he-IL', { month: 'short', year: 'numeric' }),
        monthKey: `${checkDate.getFullYear()}-${checkDate.getMonth() + 1}`
      };

      monthsToCheck.push(monthData);

      checkDate.setMonth(checkDate.getMonth() + 1);
      monthCount++;
    }


    if (monthsToCheck.length === 0) {
      monthsToCheck.push({
        month: currentMonth,
        year: currentYear,
        monthName: currentDate.toLocaleDateString('he-IL', { month: 'short', year: 'numeric' }),
        monthKey: `${currentYear}-${currentMonth}`
      });
    }

    // EXACT same payment grouping as Dashboard.jsx
    // Only count "ועד בית" payments, ignore "חד פעמי" (one-time) payments
    const paymentsByApartment = {};

    payments.forEach((payment, index) => {
      try {
        // Skip one-time payments - only count regular "ועד בית" payments
        const paymentType = payment.paymentType || payment.PaymentType || '';
        if (paymentType.includes('חד פעמי')) {
          return;
        }

        const apartmentNumber = (payment.apartmentNumber || payment.ApartmentNumber)?.toString()?.trim();
        const paymentDateValue = payment.date || payment.Date;

        if (!paymentDateValue) {
          return;
        }

        const paymentDate = safeParseDate(paymentDateValue);

        if (!paymentDate) {
          return;
        }

        const paymentMonth = paymentDate.getMonth() + 1;
        const paymentYear = paymentDate.getFullYear();

        if (apartmentNumber && !isNaN(paymentMonth) && !isNaN(paymentYear)) {
          if (!paymentsByApartment[apartmentNumber]) {
            paymentsByApartment[apartmentNumber] = new Set();
          }
          const monthKey = `${paymentYear}-${paymentMonth}`;
          paymentsByApartment[apartmentNumber].add(monthKey);
        }
      } catch (error) {
      }
    });


    // EXACT same user grouping as Dashboard.jsx
    const usersByApartment = {};

    users.forEach((user, index) => {
      try {
        const userApartment = user.apartmentNumber?.toString()?.trim();

        if (userApartment) {
          if (!usersByApartment[userApartment]) {
            usersByApartment[userApartment] = [];
          }
          usersByApartment[userApartment].push(user);
        } else {
        }
      } catch (error) {
      }
    });


    // EXACT same unpaid calculation as Dashboard.jsx
    const unpaidUsers = [];

    Object.entries(usersByApartment).forEach(([apartmentNumber, apartmentUsers]) => {

      const apartmentPayments = paymentsByApartment[apartmentNumber] || new Set();

      const unpaidMonths = [];
      const paidMonths = [];

      monthsToCheck.forEach(monthData => {
        const monthKey = monthData.monthKey;

        if (apartmentPayments.has(monthKey)) {
          paidMonths.push(monthData.monthName);
        } else {
          unpaidMonths.push(monthData.monthName);
        }
      });

      const shouldIncludeApartment = unpaidMonths.length > 0 || apartmentPayments.size === 0;

      if (shouldIncludeApartment) {
        // Use the first user as the representative for this apartment
        const representativeUser = apartmentUsers[0];
        const vaadMonthly = representativeUser?.vaadMonthly || representativeUser?.VaadMonthly || 500;

        const userData = {
          id: `apartment-${apartmentNumber}`,
          name: representativeUser?.fullName || representativeUser?.FullName || 'לא זמין',
          email: representativeUser?.email || representativeUser?.Email || '',
          apartmentNumber: apartmentNumber,
          phone: representativeUser?.phone || representativeUser?.PhoneNumber || '',
          unpaidMonths: unpaidMonths,
          paidMonths: paidMonths,
          monthsUnpaidCount: unpaidMonths.length,
          monthsPaidCount: paidMonths.length,
          totalMonthsChecked: monthsToCheck.length,
          buildingAgeMonths: monthsToCheck.length,
          vaadMonthly: vaadMonthly,
          totalDebt: unpaidMonths.length * vaadMonthly,
          usersCount: apartmentUsers.length,
          users: apartmentUsers.map(user => ({
            id: user.userID,
            name: user.fullName || user.FullName || 'לא זמין',
            email: user.email || user.Email || '',
            phone: user.phone || user.PhoneNumber || ''
          })),
          paymentStatus: unpaidMonths.length === monthsToCheck.length ? 'NO_PAYMENTS' : 'PARTIAL_PAYMENTS',
          buildingCreateDateUsed: buildingCreateDate,
          monthsCheckedList: monthsToCheck.map(m => m.monthName)
        };

        unpaidUsers.push(userData);
      } else {
      }
    });

    return unpaidUsers.sort((a, b) => {
      if (b.monthsUnpaidCount !== a.monthsUnpaidCount) {
        return b.monthsUnpaidCount - a.monthsUnpaidCount;
      }
      return parseInt(a.apartmentNumber) - parseInt(b.apartmentNumber);
    });
  };

  // Enhanced data refresh function with better error handling
  const refreshAllData = async () => {
    if (!buildingId) return;


    // Track which data sources succeeded/failed
    const dataStatus = {
      tasks: { success: false, error: null },
      messages: { success: false, error: null },
      finances: { success: false, error: null },
      users: { success: false, error: null },
      meetings: { success: false, error: null }
    };

    try {
      // Refresh tasks data with error handling
      try {
        setLoadingTasks(true);
        setTasksError(null);
        const allTasks = await getTasks(buildingId);
        if (allTasks && allTasks.tasks && allTasks.tasks.length > 0) {
          const openTasks = allTasks.tasks
            .filter(task => task.status !== "הושלם")
            .sort((a, b) => new Date(a.date) - new Date(b.date));
          setTasks(openTasks);
        } else {
          setTasks([]);
        }
        dataStatus.tasks.success = true;
      } catch (error) {
        setTasksError(`שגיאה בטעינת המשימות: ${error.message}`);
        dataStatus.tasks.error = error.message;
        setTasks([]); // Set empty array on error
      } finally {
        setLoadingTasks(false);
      }

      // Refresh messages data with error handling
      try {
        setLoadingMessages(true);
        setMessagesError(null);
        const messagesData = await getMessages(buildingId);
        setMessages(messagesData || []);
        dataStatus.messages.success = true;
      } catch (error) {
        setMessagesError(`שגיאה בטעינת ההודעות: ${error.message}`);
        dataStatus.messages.error = error.message;
        setMessages([]); // Set empty array on error
      } finally {
        setLoadingMessages(false);
      }

      // Refresh financial data with error handling
      try {
        setLoadingFinances(true);
        setFinancesError(null);

        const [payments, expenses] = await Promise.allSettled([
          getListPaymentsByBuildingId(buildingId),
          getExpensesExternalDashboard(buildingId)
        ]);

        // Handle payments result
        const paymentsArray = payments.status === 'fulfilled' ? (payments.value || []) : [];
        if (payments.status === 'rejected') {
        }

        // Handle expenses result
        const expensesArray = expenses.status === 'fulfilled' ?
          (expenses.value?.data || expenses.value || []) : [];
        if (expenses.status === 'rejected') {
        }

        setPaymentsData(paymentsArray);
        setExpensesData(expensesArray);

        // Calculate current month's financial data
        const monthlyIncome = calculateCurrentMonthIncome(paymentsArray);
        const monthlyExpenses = calculateCurrentMonthExpenses(expensesArray);

        setCurrentMonthIncome(monthlyIncome);
        setCurrentMonthExpenses(monthlyExpenses);

        // Fetch latest bank balance
        try {
          const latestBank = await getLatestBankExternal(buildingId);
          if (latestBank && latestBank.moneyLeft !== undefined) {
            setCurrentBankBalance(latestBank.moneyLeft);
          } else {
            setCurrentBankBalance(null);
          }
        } catch (bankError) {
          console.log('No bank data available');
          setCurrentBankBalance(null);
        }

        dataStatus.finances.success = true;
      } catch (error) {
        setFinancesError(`שגיאה בטעינת נתונים פיננסיים: ${error.message}`);
        dataStatus.finances.error = error.message;
        // Set default values on error
        setPaymentsData([]);
        setExpensesData([]);
        setCurrentMonthIncome(0);
        setCurrentMonthExpenses(0);
        setCurrentBankBalance(null);
      } finally {
        setLoadingFinances(false);
      }

      // Refresh unpaid users data with error handling
      try {
        setLoadingUnpaidUsers(true);
        setUnpaidUsersError(null);

        const [usersResult, paymentsResult, buildingResult] = await Promise.allSettled([
          getListUsersExternalDashboard(buildingId),
          getListPaymentsByBuildingId(buildingId),
          fetchBuildings(1, 1000, '').then(result => {
            // Find the specific building from the results
            const building = result.items?.find(b => b.buildingID === parseInt(buildingId));
            return building;
          }).catch(() => null) // Handle building fetch gracefully
        ]);

        const usersList = usersResult.status === 'fulfilled' ? (usersResult.value || []) : [];
        const paymentsArray = paymentsResult.status === 'fulfilled' ? (paymentsResult.value || []) : [];
        const buildingData = buildingResult.status === 'fulfilled' ? buildingResult.value : null;

        setUsers(usersList);

        if (usersList.length > 0 && paymentsArray.length >= 0) {
          const buildingCreateDate = buildingData?.createDate || buildingData?.CreatedDate || buildingData?.data?.createDate || null;
          const unpaid = calculateUnpaidUsers(usersList, paymentsArray, buildingCreateDate);
          setUnpaidUsers(unpaid);
        } else {
          setUnpaidUsers([]);
        }

        dataStatus.users.success = true;
      } catch (error) {
        setUnpaidUsersError(`שגיאה בטעינת נתוני חייבים: ${error.message}`);
        dataStatus.users.error = error.message;
        setUsers([]);
        setUnpaidUsers([]);
      } finally {
        setLoadingUnpaidUsers(false);
      }

      // Refresh meetings data with error handling
      try {
        setLoadingMeeting(true);
        setLoadingNextMeeting(true);
        setMeetingError(null);
        setNextMeetingError(null);

        const events = await listEvents(buildingId);

        if (events && events.length > 0) {
          const currentDate = new Date();

          // Process last meeting
          const meetingEvents = events.filter(event => {
            const isCommitteeMeeting = event.value === "ישיבת וועד";

            // Check if the meeting is completed
            const isCompleted = event.status === "הסתיים" || event.status === "completed";

            // Also include meetings that are in the past (even if status isn't explicitly "completed")
            const eventDate = new Date(event.date);
            const currentDate = new Date();

            // Create date-only comparison (ignoring time)
            const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
            const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

            // For same-day meetings, also check time if available
            let isPastMeeting = eventDateOnly < currentDateOnly;

            if (eventDateOnly.getTime() === currentDateOnly.getTime() && event.time) {
              // If it's today, check if the meeting time has passed
              const [hours, minutes] = event.time.split(':').map(num => parseInt(num, 10));
              const meetingDateTime = new Date(eventDate);
              meetingDateTime.setHours(hours, minutes, 0, 0);

              // Add some buffer time (e.g., 1 hour) to consider a meeting as "finished"
              const bufferTime = 60 * 60 * 1000; // 1 hour in milliseconds
              isPastMeeting = new Date().getTime() > (meetingDateTime.getTime() + bufferTime);
            }

            return isCommitteeMeeting && (isCompleted || isPastMeeting);
          });



          if (meetingEvents.length > 0) {
            // Sort by date descending to get the most recent meeting
            const sortedMeetings = meetingEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
            const lastCommitteeMeeting = sortedMeetings[0];


            let topics = [];
            let decisions = [];

            // Parse topics discussed
            if (lastCommitteeMeeting.topicsDiscussed) {
              if (typeof lastCommitteeMeeting.topicsDiscussed === 'string') {
                try {
                  topics = JSON.parse(lastCommitteeMeeting.topicsDiscussed);
                } catch (e) {
                  topics = lastCommitteeMeeting.topicsDiscussed.split(',').map(topic => topic.trim()).filter(topic => topic.length > 0);
                }
              } else if (Array.isArray(lastCommitteeMeeting.topicsDiscussed)) {
                topics = lastCommitteeMeeting.topicsDiscussed;
              }
            }

            // Parse decisions
            if (lastCommitteeMeeting.decisions) {
              if (typeof lastCommitteeMeeting.decisions === 'string') {
                try {
                  decisions = JSON.parse(lastCommitteeMeeting.decisions);
                } catch (e) {
                  decisions = lastCommitteeMeeting.decisions.split(',').map(decision => decision.trim()).filter(decision => decision.length > 0);
                }
              } else if (Array.isArray(lastCommitteeMeeting.decisions)) {
                decisions = lastCommitteeMeeting.decisions;
              }
            }

            setLastMeeting({
              date: safeDateFormat(lastCommitteeMeeting.date), // <- CHANGED
              time: lastCommitteeMeeting.time,
              name: lastCommitteeMeeting.name,
              topics: topics,
              decisions: decisions,
              status: lastCommitteeMeeting.status,
              location: lastCommitteeMeeting.location
            });
          } else {
            setLastMeeting(null);
          }

          // Process next meeting
          const futureMeetingEvents = events.filter(event => {
            const eventDate = new Date(event.date);
            const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
            const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
            const isCommitteeMeeting = event.value === "ישיבת וועד";
            const isFutureOrToday = eventDateOnly >= currentDateOnly;
            return isCommitteeMeeting && isFutureOrToday;
          });

          if (futureMeetingEvents.length > 0) {
            const sortedMeetings = futureMeetingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
            const nextCommitteeMeeting = sortedMeetings[0];

            let agenda = [];
            if (nextCommitteeMeeting.topicsDiscussed) {
              if (typeof nextCommitteeMeeting.topicsDiscussed === 'string') {
                try {
                  agenda = JSON.parse(nextCommitteeMeeting.topicsDiscussed);
                } catch (e) {
                  agenda = nextCommitteeMeeting.topicsDiscussed.split(',').map(topic => topic.trim()).filter(topic => topic.length > 0);
                }
              } else if (Array.isArray(nextCommitteeMeeting.topicsDiscussed)) {
                agenda = nextCommitteeMeeting.topicsDiscussed;
              }
            }

            setNextMeeting({
              date: safeDateFormat(nextCommitteeMeeting.date), // <- CHANGED
              time: nextCommitteeMeeting.time,
              name: nextCommitteeMeeting.name,
              agenda: agenda,
              status: nextCommitteeMeeting.status,
              location: nextCommitteeMeeting.location
            });
          } else {
            setNextMeeting(null);
          }
        } else {
          setLastMeeting(null);
          setNextMeeting(null);
        }

        dataStatus.meetings.success = true;
      } catch (error) {
        setMeetingError(`שגיאה בטעינת נתוני ישיבות: ${error.message}`);
        setNextMeetingError(`שגיאה בטעינת נתוני ישיבות: ${error.message}`);
        dataStatus.meetings.error = error.message;
        setLastMeeting(null);
        setNextMeeting(null);
      } finally {
        setLoadingMeeting(false);
        setLoadingNextMeeting(false);
      }

      // Log summary of data refresh
      const successCount = Object.values(dataStatus).filter(status => status.success).length;
      const totalCount = Object.keys(dataStatus).length;


      if (successCount < totalCount) {
      }

    } catch (error) {
    }
  };

  const refreshAllDataEnhanced = async () => {
    if (!buildingId) return;


    // Track which data sources succeeded/failed
    const dataStatus = {
      tasks: { success: false, error: null },
      messages: { success: false, error: null },
      finances: { success: false, error: null },
      users: { success: false, error: null },
      meetings: { success: false, error: null }
    };

    try {
      // ... (keep your existing refresh logic for other data)

      // ENHANCED unpaid users data refresh with better building date handling
      try {
        setLoadingUnpaidUsers(true);
        setUnpaidUsersError(null);

        const [usersResult, paymentsResult, buildingResult] = await Promise.allSettled([
          getListUsersExternalDashboard(buildingId),
          getListPaymentsByBuildingId(buildingId),
          fetchBuildings(1, 1000, '').then(result => {
            // Find the specific building from the results
            const building = result.items?.find(b => b.buildingID === parseInt(buildingId));
            return building;
          }).catch(() => null)
        ]);

        const usersList = usersResult.status === 'fulfilled' ? (usersResult.value || []) : [];
        const paymentsArray = paymentsResult.status === 'fulfilled' ? (paymentsResult.value || []) : [];
        const buildingData = buildingResult.status === 'fulfilled' ? buildingResult.value : null;

        setUsers(usersList);

        if (usersList.length > 0) {
          // Enhanced building creation date handling
          const buildingCreateDate = buildingData?.createDate || buildingData?.CreatedDate || buildingData?.data?.createDate || null;

          // Use the improved calculation function
          const unpaid = calculateUnpaidUsers(usersList, paymentsArray, buildingCreateDate);
          setUnpaidUsers(unpaid);
        } else {
          setUnpaidUsers([]);
        }

        dataStatus.users.success = true;
      } catch (error) {
        setUnpaidUsersError(`שגיאה בטעינת נתוני חייבים: ${error.message}`);
        dataStatus.users.error = error.message;
        setUsers([]);
        setUnpaidUsers([]);
      } finally {
        setLoadingUnpaidUsers(false);
      }

      // ... (keep the rest of your existing refresh logic)

    } catch (error) {
    }
  };

  // Auto-refresh data every 5 minutes (reduced from 1 minute to prevent browser strain)
  // Only refresh when page is visible to save resources
  useEffect(() => {
    if (!buildingId) return;

    const refreshTimer = setInterval(() => {
      if (!document.hidden && isPageVisible) {
        console.log('🔄 Auto-refreshing dashboard data (5-minute interval)');
        refreshAllData();
      }
    }, 300000); // 300000ms = 5 minutes

    return () => clearInterval(refreshTimer);
  }, [buildingId, isPageVisible]);

  // Effect to get building data from URL parameters
  useEffect(() => {
    const initializeDashboard = async () => {
      try {

        setLoadingBuilding(true);
        setBuildingError(null);

        let encryptedBuildingId = null;

        // Extract building ID from URL parameters
        const hash = window.location.hash;

        if (hash && hash.includes('?')) {
          const queryString = hash.split('?')[1];
          const urlParams = new URLSearchParams(queryString);
          encryptedBuildingId = urlParams.get('bid');
        }

        // Fallback: Check regular URL search params
        if (!encryptedBuildingId) {
          const urlParams = new URLSearchParams(window.location.search);
          encryptedBuildingId = urlParams.get('bid');
        }

        if (!encryptedBuildingId) {
          // Fallback to localStorage for backwards compatibility
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              if (user.buildingId) {
                setBuildingId(user.buildingId);
                setBuilding({
                  address: "כתובת הבניין",
                  buildingID: user.buildingId
                });
                return;
              }
            } catch (e) {
            }
          }

          setBuildingError('לא נמצא מזהה בניין. יש להשתמש בקישור תקין או להתחבר למערכת.');
          return;
        }

        // Get building data using encrypted ID

        try {
          const [buildingData, buildingsListData] = await Promise.allSettled([
            getBuildingDataExternal(encryptedBuildingId),
            fetchBuildings(1, 1000, '').catch(() => null) // Get building list to find creation date
          ]);


          if (buildingData.status === 'fulfilled' && buildingData.value && buildingData.value.success) {
            const bData = buildingData.value;
            setBuildingId(bData.buildingId);
            setBuilding({
              buildingID: bData.buildingId,
              address: bData.address,
              addressNumber: bData.addressNumber,
              city: bData.city,
              numFloors: bData.numFloors,
              numApartments: bData.numApartments
            });

            // Try to get building creation date from buildings list
            if (buildingsListData.status === 'fulfilled' && buildingsListData.value) {
              const fullBuildingData = buildingsListData.value.items?.find(b => b.buildingID === parseInt(bData.buildingId));
              if (fullBuildingData?.createDate) {
                setBuildingCreateDate(fullBuildingData.createDate);
              }
            }

          } else {
            setBuildingError(`שגיאה בטעינת נתוני הבניין: ${buildingData?.value?.message || 'תגובה לא תקינה מהשרת'}`);
          }
        } catch (apiError) {
          setBuildingError(`שגיאה בקריאה לשרת: ${apiError.message}`);
        }

      } catch (error) {
        setBuildingError(`שגיאה כללית: ${error.message}`);
      } finally {
        setLoadingBuilding(false);
      }
    };

    initializeDashboard();
  }, []);

  // Function to handle unpaid user row click
  const handleUnpaidUserClick = (user) => {
    setSelectedUnpaidUser(user);
    setIsUnpaidModalOpen(true);
  };

  // Function to close modal
  const closeUnpaidModal = () => {
    setIsUnpaidModalOpen(false);
    setSelectedUnpaidUser(null);
  };

  // Function to handle reminder sent callback
  const handleReminderSent = (user, status) => {
    if (status === 'success') {
      setReminderNotification({
        type: 'success',
        message: `תזכורת נשלחה בהצלחה ל${user.name}`
      });
    } else {
      setReminderNotification({
        type: 'error',
        message: `שגיאה בשליחת תזכורת ל${user.name}`
      });
    }

    setTimeout(() => {
      setReminderNotification(null);
    }, 5000);
  };

  // Priority classes for unpaid users based on actual months unpaid
  const getUnpaidPriorityClass = (monthsCount) => {
    const count = monthsCount || 0;
    if (count >= 2) return { backgroundColor: '#fef2f2', color: '#dc2626' };
    if (count === 1) return { backgroundColor: '#fef9c3', color: '#a16207' };
    return { backgroundColor: '#f3f4f6', color: '#374151' };
  };

  // Priority text for unpaid users - show actual count
  const getUnpaidPriorityText = (monthsCount) => {
    const count = monthsCount || 0;
    if (count === 0) return 'לא זמין';
    if (count === 1) return '1 חודש';
    return `${count} חודשים`;
  };

  // Fetch financial data - dependent on buildingId state
  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!buildingId) return;

      try {
        setLoadingFinances(true);
        setFinancesError(null);

        const [payments, expenses] = await Promise.all([
          getListPaymentsByBuildingId(buildingId),
          getExpensesExternalDashboard(buildingId)
        ]);

        const paymentsArray = payments || [];
        const expensesArray = expenses?.data || expenses || [];

        setPaymentsData(paymentsArray);
        setExpensesData(expensesArray);

        // Calculate current month's financial data
        const monthlyIncome = calculateCurrentMonthIncome(paymentsArray);
        const monthlyExpenses = calculateCurrentMonthExpenses(expensesArray);

        setCurrentMonthIncome(monthlyIncome);
        setCurrentMonthExpenses(monthlyExpenses);

        // Fetch latest bank balance
        try {
          const latestBank = await getLatestBankExternal(buildingId);
          if (latestBank && latestBank.moneyLeft !== undefined) {
            setCurrentBankBalance(latestBank.moneyLeft);
          } else {
            setCurrentBankBalance(null);
          }
        } catch (bankError) {
          console.log('No bank data available');
          setCurrentBankBalance(null);
        }

      } catch (error) {
        setFinancesError(`שגיאה בטעינת נתונים פיננסיים: ${error.message}`);
        setCurrentBankBalance(null);
      } finally {
        setLoadingFinances(false);
      }
    };

    fetchFinancialData();
  }, [buildingId]);

  // Fetch tasks data - dependent on buildingId state
  useEffect(() => {
    const fetchTasks = async () => {
      if (!buildingId) return;

      try {
        setLoadingTasks(true);
        setTasksError(null);

        const allTasks = await getTasks(buildingId);

        if (allTasks && allTasks.tasks && allTasks.tasks.length > 0) {
          const openTasks = allTasks.tasks
            .filter(task => task.status !== "הושלם")
            .sort((a, b) => new Date(a.date) - new Date(b.date));

          setTasks(openTasks);
        } else {
          setTasks([]);
        }
      } catch (error) {
        setTasksError(`שגיאה בטעינת המשימות: ${error.message}`);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchTasks();
  }, [buildingId]);

  // Fetch messages data - dependent on buildingId state
  useEffect(() => {
    const fetchMessages = async () => {
      if (!buildingId) return;

      try {
        setLoadingMessages(true);
        setMessagesError(null);

        const messagesData = await getMessages(buildingId);

        setMessages(messagesData || []);
      } catch (error) {
        setMessagesError(`שגיאה בטעינת ההודעות: ${error.message}`);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [buildingId]);

  // Fetch unpaid users data - dependent on buildingId state
  useEffect(() => {
    const fetchUnpaidUsersData = async () => {
      if (!buildingId) return;

      try {
        setLoadingUnpaidUsers(true);
        setUnpaidUsersError(null);

        const [usersList, payments, buildingData] = await Promise.all([
          getListUsersExternalDashboard(buildingId),
          getListPaymentsByBuildingId(buildingId),
          fetchBuildings(1, 1000, '').then(result => {
            // Find the specific building from the results
            const building = result.items?.find(b => b.buildingID === parseInt(buildingId));
            return building;
          }).catch(() => null) // Handle building fetch gracefully
        ]);

        setUsers(usersList || []);

        if (usersList && payments) {
          const buildingCreateDate = buildingData?.createDate || null;
          const unpaid = calculateUnpaidUsers(usersList, payments, buildingCreateDate);
          setUnpaidUsers(unpaid);
        }

      } catch (error) {
        setUnpaidUsersError(`שגיאה בטעינת נתוני חייבים: ${error.message}`);
      } finally {
        setLoadingUnpaidUsers(false);
      }
    };

    fetchUnpaidUsersData();
  }, [buildingId]);

  // Utility function for safe date parsing
  const safeParseDate = (dateValue, fallback = null) => {
    if (!dateValue) return fallback;

    // Handle different date formats
    const date = new Date(dateValue);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return fallback;
    }

    return date;
  };

  // Utility function for safe date formatting
  const safeDateFormat = (dateValue, locale = 'he-IL', options = {}) => {
    const date = safeParseDate(dateValue);
    if (!date) return 'לא זמין';

    try {
      return date.toLocaleDateString(locale, options);
    } catch (error) {
      return 'לא זמין';
    }
  };
  // Fetch meetings data - dependent on buildingId state
  useEffect(() => {
    const fetchMeetingsData = async () => {
      if (!buildingId) return;

      try {
        setLoadingMeeting(true);
        setLoadingNextMeeting(true);
        setMeetingError(null);
        setNextMeetingError(null);

        const events = await listEvents(buildingId);

        if (events && events.length > 0) {
          const currentDate = new Date();

          // Process last meeting
          const meetingEvents = events.filter(event => {
            const isCommitteeMeeting = event.value === "ישיבת וועד";

            // Check if the meeting is completed
            const isCompleted = event.status === "הסתיים" || event.status === "completed";

            // Also include meetings that are in the past (even if status isn't explicitly "completed")
            const eventDate = new Date(event.date);

            // Create date-only comparison (ignoring time)
            const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
            const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

            // For same-day meetings, also check time if available
            let isPastMeeting = eventDateOnly < currentDateOnly;

            if (eventDateOnly.getTime() === currentDateOnly.getTime() && event.time) {
              // If it's today, check if the meeting time has passed
              const [hours, minutes] = event.time.split(':').map(num => parseInt(num, 10));
              const meetingDateTime = new Date(eventDate);
              meetingDateTime.setHours(hours, minutes, 0, 0);

              // Add some buffer time (e.g., 1 hour) to consider a meeting as "finished"
              const bufferTime = 60 * 60 * 1000; // 1 hour in milliseconds
              isPastMeeting = new Date().getTime() > (meetingDateTime.getTime() + bufferTime);
            }

            return isCommitteeMeeting && (isCompleted || isPastMeeting);
          });

          if (meetingEvents.length > 0) {
            // Sort by date descending to get the most recent meeting
            const sortedMeetings = meetingEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
            const lastCommitteeMeeting = sortedMeetings[0];

            let topics = [];
            let decisions = [];

            // Parse topics discussed
            if (lastCommitteeMeeting.topicsDiscussed) {
              if (typeof lastCommitteeMeeting.topicsDiscussed === 'string') {
                try {
                  topics = JSON.parse(lastCommitteeMeeting.topicsDiscussed);
                } catch (e) {
                  topics = lastCommitteeMeeting.topicsDiscussed.split(',').map(topic => topic.trim()).filter(topic => topic.length > 0);
                }
              } else if (Array.isArray(lastCommitteeMeeting.topicsDiscussed)) {
                topics = lastCommitteeMeeting.topicsDiscussed;
              }
            }

            // Parse decisions
            if (lastCommitteeMeeting.decisions) {
              if (typeof lastCommitteeMeeting.decisions === 'string') {
                try {
                  decisions = JSON.parse(lastCommitteeMeeting.decisions);
                } catch (e) {
                  decisions = lastCommitteeMeeting.decisions.split(',').map(decision => decision.trim()).filter(decision => decision.length > 0);
                }
              } else if (Array.isArray(lastCommitteeMeeting.decisions)) {
                decisions = lastCommitteeMeeting.decisions;
              }
            }

            setLastMeeting({
              date: safeDateFormat(lastCommitteeMeeting.date),
              time: lastCommitteeMeeting.time,
              name: lastCommitteeMeeting.name,
              topics: topics,
              decisions: decisions,
              status: lastCommitteeMeeting.status,
              location: lastCommitteeMeeting.location
            });
          } else {
            setLastMeeting(null);
          }

          // Process next meeting
          const futureMeetingEvents = events.filter(event => {
            const eventDate = new Date(event.date);
            const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
            const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
            const isCommitteeMeeting = event.value === "ישיבת וועד";
            const isFutureOrToday = eventDateOnly >= currentDateOnly;
            return isCommitteeMeeting && isFutureOrToday;
          });

          if (futureMeetingEvents.length > 0) {
            const sortedMeetings = futureMeetingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
            const nextCommitteeMeeting = sortedMeetings[0];

            let agenda = [];
            if (nextCommitteeMeeting.topicsDiscussed) {
              if (typeof nextCommitteeMeeting.topicsDiscussed === 'string') {
                try {
                  agenda = JSON.parse(nextCommitteeMeeting.topicsDiscussed);
                } catch (e) {
                  agenda = nextCommitteeMeeting.topicsDiscussed.split(',').map(topic => topic.trim()).filter(topic => topic.length > 0);
                }
              } else if (Array.isArray(nextCommitteeMeeting.topicsDiscussed)) {
                agenda = nextCommitteeMeeting.topicsDiscussed;
              }
            }

            setNextMeeting({
              date: safeDateFormat(nextCommitteeMeeting.date),
              time: nextCommitteeMeeting.time,
              name: nextCommitteeMeeting.name,
              agenda: agenda,
              status: nextCommitteeMeeting.status,
              location: nextCommitteeMeeting.location
            });
          } else {
            setNextMeeting(null);
          }
        } else {
          setLastMeeting(null);
          setNextMeeting(null);
        }

      } catch (error) {
        setMeetingError(`שגיאה בטעינת נתוני ישיבות: ${error.message}`);
        setNextMeetingError(`שגיאה בטעינת נתוני ישיבות: ${error.message}`);
        setLastMeeting(null);
        setNextMeeting(null);
      } finally {
        setLoadingMeeting(false);
        setLoadingNextMeeting(false);
      }
    };

    fetchMeetingsData();
  }, [buildingId]);

  // Get priority badge style
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'גבוה':
        return { backgroundColor: "#fef2f2", color: "#dc2626" };
      case 'בינוני':
        return { backgroundColor: "#fef9c3", color: "#a16207" };
      case 'נמוך':
        return { backgroundColor: "#f0fdf4", color: "#16a34a" };
      default:
        return { backgroundColor: "#f3f4f6", color: "#374151" };
    }
  };

  // Show loading or error states for building data
  if (loadingBuilding) {
    return (
      <div style={{
        backgroundColor: "#f3f4f6",
        padding: "2rem",
        height: "100vh",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }} dir="rtl">
        <div>
          <RefreshCw size={64} style={{ animation: 'spin 2s linear infinite', marginBottom: '2rem' }} />
          <h2 style={{ fontSize: '2rem' }}>טוען נתוני לוח הבקרה...</h2>
        </div>
      </div>
    );
  }

  if (buildingError) {
    return (
      <div style={{
        backgroundColor: "#f3f4f6",
        padding: "2rem",
        height: "100vh",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }} dir="rtl">
        <div style={{
          backgroundColor: "white",
          borderRadius: "1rem",
          padding: "3rem",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          maxWidth: "600px"
        }}>
          <AlertTriangle size={64} style={{ color: '#dc2626', marginBottom: '2rem' }} />
          <h2 style={{ color: '#dc2626', marginBottom: '1.5rem', fontSize: '2rem' }}>שגיאה בטעינת לוח הבקרה</h2>
          <p style={{ color: '#6b7280', fontSize: '1.25rem' }}>{buildingError}</p>
        </div>
      </div>
    );
  }

  // TV-Optimized Compact Styles for 1920×1080 and higher
  const tvStyles = {
    container: {
      backgroundColor: "#f8fafc",
      padding: "0.75rem",
      height: "100vh",
      width: "100vw",
      overflow: "hidden",
      textAlign: "right",
      fontFamily: "system-ui, -apple-system, sans-serif",
      display: "flex",
      gap: "0.75rem"
    },
    leftSidebar: {
      width: "300px",
      backgroundColor: "white",
      borderRadius: "0.75rem",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      border: "1px solid #f1f5f9",
      padding: "1rem",
      display: "flex",
      flexDirection: "column",
      height: "calc(100vh - 1.5rem)"
    },
    mainContent: {
      flex: 1,
      display: "flex",
      flexDirection: "column"
    },
    header: {
      marginBottom: "0.75rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "2px solid #e2e8f0",
      paddingBottom: "0.5rem"
    },
    title: {
      fontSize: "2.1rem",
      fontWeight: "800",
      color: "#1e3a8a",
      margin: 0,
      textShadow: "0 2px 4px rgba(0,0,0,0.1)"
    },
    timeDisplay: {
      color: "#475569",
      display: "flex",
      alignItems: "center",
      fontSize: "1.4rem",
      fontWeight: "600",
      gap: "0.75rem",
      backgroundColor: "white",
      padding: "0.5rem 1rem",
      borderRadius: "0.75rem",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    },
    icon: {
      width: "1.5rem",
      height: "1.5rem"
    },
    separator: {
      margin: "0 0.5rem",
      color: "#cbd5e1"
    },
    // Financial Statistics Grid - Horizontal Layout
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "0.75rem",
      marginBottom: "0.75rem",
      height: "105px"
    },
    statsCard: {
      backgroundColor: "white",
      borderRadius: "0.75rem",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      padding: "1rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: "1px solid #f1f5f9",
      transition: "all 0.3s ease"
    },
    statContent: {
      display: "flex",
      flexDirection: "column",
      gap: "0.25rem"
    },
    statLabel: {
      fontSize: "1.2rem",
      color: "#64748b",
      fontWeight: "600",
      margin: 0
    },
    statValue: {
      fontSize: "1.7rem",
      fontWeight: "800",
      color: "#0f172a",
      margin: 0
    },
    // Main Content Grid - 2x3 Layout for 5 sections (unpaid users moved to sidebar)
    mainGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gridTemplateRows: "1fr 1fr",
      gap: "0.75rem",
      height: "calc(-224px + 100vh)"
    },
    card: {
      backgroundColor: "white",
      borderRadius: "0.75rem",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      padding: "1rem",
      border: "1px solid #f1f5f9",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      marginBottom: "0.75rem",
      gap: "0.5rem",
      borderBottom: "1px solid #f1f5f9",
      paddingBottom: "0.5rem"
    },
    cardTitle: {
      fontSize: "1.4rem",
      fontWeight: "700",
      margin: 0,
      color: "#1e293b"
    },
    cardContent: {
      flex: 1,
      overflow: "hidden"
    },
    // Icons with specific colors
    iconGreen: { color: "#16a34a" },
    iconRed: { color: "#dc2626" },
    iconPurple: { color: "#7c3aed" },
    iconOrange: { color: "#ea580c" },
    iconBlue: { color: "#3b82f6" },
    iconTeal: { color: "#0d9488" },
    // Meeting Styles
    nextMeetingContainer: {
      display: "flex",
      gap: "0.75rem",
      height: "100%"
    },
    meetingDate: {
      backgroundColor: "#fef2f2",
      color: "#b91c1c",
      borderRadius: "0.75rem",
      padding: "0.75rem",
      textAlign: "center",
      minWidth: "120px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      border: "1px solid #fca5a5"
    },
    meetingDateText: {
      fontSize: "1.7rem",
      fontWeight: "800",
      margin: 0
    },
    meetingTime: {
      fontSize: "1.7rem",
      fontWeight: "600",
      margin: "0.25rem 0 0 0"
    },
    meetingAgenda: {
      flex: 1,
      overflow: "auto"
    },
    listTitle: {
      fontWeight: "700",
      marginBottom: "0.5rem",
      fontSize: "1.7rem",
      color: "#374151"
    },
    bulletedList: {
      listStyleType: "disc",
      paddingRight: "1.5rem",
      fontSize: "1.7rem",
      lineHeight: "1.4",
      color: "#4b5563"
    },
    listItem: {
      marginBottom: "0.5rem"
    },
    // Messages Styles
    messageContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden"
    },
    messageCard: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "0.75rem",
      padding: "1rem",
      margin: "0.25rem 0",
      transition: "all 0.5s ease-in-out"
    },
    messageTitle: {
      fontSize: "1.7rem",
      fontWeight: "700",
      color: "#1e293b",
      marginBottom: "0.5rem",
      textAlign: "center"
    },
    messageBody: {
      fontSize: "1.7rem",
      lineHeight: "1.4",
      color: "#475569",
      textAlign: "right",
      wordWrap: "break-word"
    },
    messageDate: {
      fontSize: "1.1rem",
      color: "#64748b",
      textAlign: "left",
      marginTop: "0.5rem",
      fontStyle: "italic"
    },
    messageIndicator: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "0.25rem",
      marginTop: "0.5rem"
    },
    messageDot: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: "#cbd5e1",
      transition: "background-color 0.3s ease"
    },
    messageDotActive: {
      backgroundColor: "#3b82f6"
    },
    // Table Styles with Auto-Scroll
    tableContainer: {
      flex: 1,
      overflow: "hidden",
      position: "relative",
      height: "100%"
    },
    tableScrollContainer: {
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden"
    },
    table: {
      width: "100%",
      fontSize: "1.15rem",
      borderCollapse: "collapse"
    },
    tableHeader: {
      backgroundColor: "#f8fafc",
      borderBottom: "2px solid #e2e8f0",
      position: "sticky",
      top: 0,
      zIndex: 2
    },
    tableHeaderCell: {
      padding: "0.75rem",
      textAlign: "right",
      fontWeight: "700",
      fontSize: "1.2rem",
      color: "#374151",
      height: "45px"
    },
    tableRow: {
      borderBottom: "1px solid #f1f5f9",
      transition: "background-color 0.2s ease",
      height: "70px" // Fixed height for consistent scrolling
    },
    tableCell: {
      padding: "0.75rem",
      fontSize: "1.15rem",
      color: "#4b5563",
      verticalAlign: "middle",
      height: "70px"
    },
    clickableRow: {
      cursor: "pointer"
    },
    statusBadge: {
      padding: "0.25rem 0.5rem",
      borderRadius: "0.375rem",
      fontSize: "1.05rem",
      fontWeight: "600",
      textAlign: "center",
      minWidth: "60px",
      display: "inline-block"
    },
    priorityBadge: {
      padding: "0.25rem 0.5rem",
      borderRadius: "0.375rem",
      fontSize: "1.05rem",
      fontWeight: "600",
      textAlign: "center",
      minWidth: "60px",
      display: "inline-block"
    },
    statusCompleted: {
      backgroundColor: "#dcfce7",
      color: "#15803d"
    },
    statusInProgress: {
      backgroundColor: "#fef9c3",
      color: "#a16207"
    },
    statusPending: {
      backgroundColor: "#dbeafe",
      color: "#1d4ed8"
    },
    loadingMessage: {
      color: "#6b7280",
      fontStyle: "italic",
      textAlign: "center",
      fontSize: "1.3rem",
      padding: "1rem"
    },
    errorMessage: {
      color: "#dc2626",
      textAlign: "center",
      fontSize: "1.3rem",
      padding: "1rem"
    },
    emptyState: {
      textAlign: "center",
      fontSize: "1.4rem",
      color: "#059669",
      fontWeight: "600",
      padding: "1rem"
    },
    reminderNotification: {
      position: "fixed",
      top: "1rem",
      right: "1rem",
      zIndex: 1000,
      padding: "1rem 1.5rem",
      borderRadius: "0.75rem",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.2)",
      fontSize: "1.3rem",
      fontWeight: "600"
    },
    notificationSuccess: {
      backgroundColor: "#dcfce7",
      color: "#15803d",
      border: "2px solid #bbf7d0"
    },
    notificationError: {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      border: "2px solid #fecaca"
    }
  };

  return (
    <>
      {/* CSS Animation for pulse effect */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      <div style={tvStyles.container} dir="rtl">
        {/* Reminder Notification */}
        {reminderNotification && (
          <div style={{
            ...tvStyles.reminderNotification,
            ...(reminderNotification.type === 'success' ? tvStyles.notificationSuccess : tvStyles.notificationError)
          }}>
            <span>{reminderNotification.message}</span>
            <button
              onClick={() => setReminderNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            >
              <X size={24} />
            </button>
          </div>
        )}

        {/* Left Sidebar - Unpaid Users */}
        <div style={tvStyles.leftSidebar}>
          <div style={{
            ...tvStyles.cardHeader,
            marginBottom: "1rem",
            borderBottom: "2px solid #e2e8f0",
            paddingBottom: "0.75rem"
          }}>
            <AlertTriangle style={{ ...tvStyles.icon, ...tvStyles.iconOrange }} />
            <h2 style={{
              ...tvStyles.cardTitle,
              fontSize: "1.7rem"
            }}>{unpaidUsers.length} דיירים חייבים</h2>
          </div>
          
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column"
          }}>
            {loadingUnpaidUsers ? (
              <div style={tvStyles.loadingMessage}>טוען נתוני חייבים...</div>
            ) : unpaidUsersError ? (
              <div style={tvStyles.errorMessage}>{unpaidUsersError}</div>
            ) : unpaidUsers.length > 0 ? (
              <div style={{
                ...tvStyles.tableContainer,
                flex: 1
              }}>
                <div
                  ref={unpaidUsersScroll.containerRef}
                  style={{
                    ...tvStyles.tableScrollContainer,
                    height: "100%",
                    maxHeight: "calc(100vh - 150px)"
                  }}
                >
                  <div style={{ position: "relative", overflow: "hidden" }}>
                    <div style={{
                      width: "100%",
                      position: "relative"
                    }}>
                      <div style={{
                        ...tvStyles.tableHeader,
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        backgroundColor: "#f8fafc",
                        display: "flex",
                        borderRadius: "0.5rem 0.5rem 0 0",
                        marginBottom: "0.5rem"
                      }}>
                        <div style={{...tvStyles.tableHeaderCell, flex: 1}}>דירה</div>
                        <div style={{...tvStyles.tableHeaderCell, flex: 1}}>חודשים</div>
                      </div>
                      <div 
                        data-scroll-container
                        style={{ 
                          position: "relative",
                          willChange: "transform",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          width: "100%"
                        }}>
                      {unpaidUsers
                        .sort((a, b) => {
                          const apartmentA = parseInt(a.apartmentNumber) || 0;
                          const apartmentB = parseInt(b.apartmentNumber) || 0;
                          return apartmentA - apartmentB;
                        })
                        .map((user, index) => (
                          <div
                            key={`user-${user.id}-${index}`}
                            style={{
                              borderBottom: 'none',
                              backgroundColor: 'white',
                              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)',
                              border: '1px solid rgba(59, 130, 246, 0.15)',
                              borderRadius: '0.75rem',
                              display: 'flex',
                              width: '100%',
                              minHeight: '65px',
                              margin: '0',
                              padding: '0.5rem',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              background: 'linear-gradient(to bottom, #ffffff, #fafbfc)',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <span style={{
                              fontSize: '1.5rem',
                              fontWeight: '600',
                              color: '#1e293b'
                            }}>
                              דירה {user.apartmentNumber}
                            </span>
                            <span
                              style={{
                                ...tvStyles.priorityBadge,
                                ...getUnpaidPriorityClass(user.monthsUnpaidCount),
                                fontSize: '0.9rem',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '1rem',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                              }}
                            >
                              {getUnpaidPriorityText(user.monthsUnpaidCount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                ...tvStyles.emptyState,
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                🎉 כל הדיירים שילמו בזמן!
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div style={tvStyles.mainContent}>
          {/* Header */}
        <header style={tvStyles.header}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem"
          }}>
            <div style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)",
              color: "white",
              padding: "0.5rem 1rem",
              borderRadius: "0.75rem",
              fontSize: "1.6rem",
              fontWeight: "900",
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              border: "2px solid rgba(255,255,255,0.2)",
              letterSpacing: "0.5px"
            }}>
              BILDIAPP
            </div>
            <h1 style={tvStyles.title}>
              {building?.city || 'לוח הבקרה'} - {building?.address || ''} - {building?.addressNumber || ''}
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {currentBankBalance !== null && (
              <div style={{
                color: "#059669",
                display: "flex",
                alignItems: "center",
                fontSize: "1.4rem",
                fontWeight: "700",
                gap: "0.5rem",
                backgroundColor: "#d1fae5",
                padding: "0.5rem 1rem",
                borderRadius: "0.75rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #6ee7b7"
              }}>
                <span>💰</span>
                <span>יתרה נוכחית: ₪{currentBankBalance.toLocaleString()}</span>
              </div>
            )}
            <div style={tvStyles.timeDisplay}>
              <Clock style={tvStyles.icon} />
              <span>{currentTime.toLocaleTimeString('he-IL')}</span>
              <span style={tvStyles.separator}>|</span>
              <Calendar style={tvStyles.icon} />
              <span>{currentTime.toLocaleDateString('he-IL')}</span>
            </div>
          </div>
        </header>

        {/* Financial Statistics - Horizontal Layout */}
        <div style={tvStyles.statsGrid}>
          {/* Current Month Income */}
          <div style={tvStyles.statsCard}>
            <div style={tvStyles.statContent}>
              <p style={tvStyles.statLabel}>הכנסות החודש</p>
              <p style={tvStyles.statValue}>
                {loadingFinances ? 'טוען...' : `₪${currentMonthIncome.toLocaleString()}`}
              </p>
            </div>
            <div>
              <DollarSign size={36} style={tvStyles.iconGreen} />
            </div>
          </div>

          {/* Current Month Expenses */}
          <div style={tvStyles.statsCard}>
            <div style={tvStyles.statContent}>
              <p style={tvStyles.statLabel}>הוצאות החודש</p>
              <p style={tvStyles.statValue}>
                {loadingFinances ? 'טוען...' : `₪${currentMonthExpenses.toLocaleString()}`}
              </p>
            </div>
            <div>
              <CreditCard size={36} style={tvStyles.iconRed} />
            </div>
          </div>

          {/* Net Income */}
          <div style={tvStyles.statsCard}>
            <div style={tvStyles.statContent}>
              <p style={tvStyles.statLabel}>יתרה</p>
              <p style={{
                ...tvStyles.statValue,
                color: (currentMonthIncome - currentMonthExpenses) >= 0 ? "#16a34a" : "#dc2626"
              }}>
                {loadingFinances ? 'טוען...' : `₪${(currentMonthIncome - currentMonthExpenses).toLocaleString()}`}
              </p>
            </div>
            <div>
              <BarChart2 size={36} style={
                (currentMonthIncome - currentMonthExpenses) >= 0 ? tvStyles.iconGreen : tvStyles.iconRed
              } />
            </div>
          </div>

          {/* Total Apartments */}
          <div style={tvStyles.statsCard}>
            <div style={tvStyles.statContent}>
              <p style={tvStyles.statLabel}>סה"כ דירות</p>
              <p style={tvStyles.statValue}>
                {loadingUnpaidUsers ? 'טוען...' : building?.numApartments || new Set(users.map(u => u.apartmentNumber)).size}
              </p>
            </div>
            <div>
              <Users size={36} style={tvStyles.iconBlue} />
            </div>
          </div>
        </div>

        {/* Financial Error Display */}
        {financesError && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '2px solid #fecaca',
            borderRadius: '1rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#dc2626',
            fontSize: '1.425rem'
          }}>
            {financesError}
          </div>
        )}

        {/* Main Content Grid - 2x3 Layout for 5 sections */}
        <div style={tvStyles.mainGrid}>
          {/* Next Meeting */}
          <div style={tvStyles.card}>
            <div style={tvStyles.cardHeader}>
              <Calendar style={{ ...tvStyles.icon, ...tvStyles.iconRed }} />
              <h2 style={tvStyles.cardTitle}>ישיבת דיירים הבאה</h2>
            </div>
            <div style={tvStyles.cardContent}>
              {loadingNextMeeting ? (
                <div style={tvStyles.loadingMessage}>טוען נתוני ישיבה הבאה...</div>
              ) : nextMeetingError ? (
                <div style={tvStyles.errorMessage}>{nextMeetingError}</div>
              ) : nextMeeting ? (
                <div style={tvStyles.nextMeetingContainer}>
                  <div style={tvStyles.meetingDate}>
                    <p style={tvStyles.meetingDateText}>{nextMeeting.date}</p>
                    <p style={tvStyles.meetingTime}>{nextMeeting.time}</p>
                  </div>
                  <div style={tvStyles.meetingAgenda}>
                    <h3 style={tvStyles.listTitle}>על סדר היום:</h3>
                    {nextMeeting.agenda.length > 0 ? (
                      <ul style={tvStyles.bulletedList}>
                        {nextMeeting.agenda.map((item, index) => (
                          <li key={index} style={tvStyles.listItem}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p style={tvStyles.loadingMessage}>אין נושאים על סדר היום</p>
                    )}
                  </div>
                </div>
              ) : (
                <div style={tvStyles.loadingMessage}>לא נמצאו ישיבות ועד עתידיות</div>
              )}
            </div>
          </div>

          {/* Last Meeting */}
          <div style={tvStyles.card}>
            <div style={tvStyles.cardHeader}>
              <Users style={{ ...tvStyles.icon, ...tvStyles.iconPurple }} />
              <h2 style={tvStyles.cardTitle}>
                ישיבת ועד אחרונה {lastMeeting && `- ${lastMeeting.date}`}
              </h2>
            </div>
            <div style={tvStyles.cardContent}>
              {loadingMeeting ? (
                <div style={tvStyles.loadingMessage}>טוען נתוני ישיבה...</div>
              ) : meetingError ? (
                <div style={tvStyles.errorMessage}>{meetingError}</div>
              ) : lastMeeting ? (
                <div style={{ overflow: "auto", height: "100%" }}>
                  <h3 style={tvStyles.listTitle}>נושאים שנדונו:</h3>
                  {lastMeeting.topics.length > 0 ? (
                    <ul style={tvStyles.bulletedList}>
                      {lastMeeting.topics.map((topic, index) => (
                        <li key={index} style={tvStyles.listItem}>{topic}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={tvStyles.loadingMessage}>לא נמצאו נושאים</p>
                  )}

                  <h3 style={tvStyles.listTitle}>החלטות:</h3>
                  {lastMeeting.decisions.length > 0 ? (
                    <ul style={tvStyles.bulletedList}>
                      {lastMeeting.decisions.map((decision, index) => (
                        <li key={index} style={tvStyles.listItem}>{decision}</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={tvStyles.loadingMessage}>לא נמצאו החלטות</p>
                  )}
                </div>
              ) : (
                <div style={tvStyles.loadingMessage}>לא נמצאו ישיבות ועד קודמות</div>
              )}
            </div>
          </div>

          {/* Tasks Table */}
          <div style={tvStyles.card}>
            <div style={tvStyles.cardHeader}>
              <CheckCircle style={{ ...tvStyles.icon, ...tvStyles.iconGreen }} />
              <h2 style={tvStyles.cardTitle}>משימות פתוחות ({tasks.length})</h2>
            </div>
            <div style={tvStyles.cardContent}>
              {loadingTasks ? (
                <div style={tvStyles.loadingMessage}>טוען משימות...</div>
              ) : tasksError ? (
                <div style={tvStyles.errorMessage}>{tasksError}</div>
              ) : tasks.length > 0 ? (
                <div style={tvStyles.tableContainer}>
                  <div
                    ref={tasksScroll.containerRef}
                    style={tvStyles.tableScrollContainer}
                  >
                    <table style={tvStyles.table}>
                      <thead style={tvStyles.tableHeader}>
                        <tr>
                          <th style={tvStyles.tableHeaderCell}>תיאור</th>
                          <th style={tvStyles.tableHeaderCell}>אחראי</th>
                          <th style={tvStyles.tableHeaderCell}>סטטוס</th>
                          <th style={tvStyles.tableHeaderCell}>תאריך יעד</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, index) => (
                          <tr key={`${task.TaskID || task.id}-${index}`} style={tvStyles.tableRow}>
                            <td style={tvStyles.tableCell}>{task.description}</td>
                            <td style={tvStyles.tableCell}>{task.taskUser || 'לא צוין'}</td>
                            <td style={tvStyles.tableCell}>
                              <span style={{
                                ...tvStyles.statusBadge,
                                ...(task.Status === 'הושלם' ? tvStyles.statusCompleted :
                                  task.Status === 'בתהליך' ? tvStyles.statusInProgress :
                                    tvStyles.statusPending)
                              }}>
                                {task.status}
                              </span>
                            </td>
                            <td style={tvStyles.tableCell}>
                              {safeDateFormat(task.date, 'he-IL') || 'לא צוין'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={tvStyles.emptyState}>אין משימות פתוחות</div>
              )}
            </div>
          </div>

          {/* Messages Section */}
          <div style={{
            ...tvStyles.card,
            gridColumn: "span 2"
          }}>
            <div style={tvStyles.cardHeader}>
              <MessageSquare style={{ ...tvStyles.icon, ...tvStyles.iconTeal }} />
              <h2 style={tvStyles.cardTitle}>הודעות חשובות ({messages.length})</h2>
            </div>
            <div style={tvStyles.cardContent}>
              {loadingMessages ? (
                <div style={tvStyles.loadingMessage}>טוען הודעות...</div>
              ) : messagesError ? (
                <div style={tvStyles.errorMessage}>{messagesError}</div>
              ) : messages.length > 0 ? (
                <div style={tvStyles.messageContainer}>
                  {messages.map((message, index) => (
                    <div
                      key={message.id || index}
                      style={{
                        ...tvStyles.messageCard,
                        display: index === currentMessageIndex ? 'block' : 'none'
                      }}
                    >
                      <h3 style={tvStyles.messageTitle}>{message.title}</h3>
                      <div style={tvStyles.messageBody}>
                        {message.body}
                      </div>
                      <div style={tvStyles.messageDate}>
                        {safeDateFormat(message.createDate, 'he-IL', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Message indicator dots */}
                  {messages.length > 1 && (
                    <div style={tvStyles.messageIndicator}>
                      {messages.map((_, index) => (
                        <div
                          key={index}
                          style={{
                            ...tvStyles.messageDot,
                            ...(index === currentMessageIndex ? tvStyles.messageDotActive : {})
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={tvStyles.emptyState}>אין הודעות חשובות</div>
              )}
            </div>
          </div>

          {/* QR Code Section for App Access */}
          <div style={tvStyles.card}>
            <div style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "0.75rem",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.75rem"
            }}>
              <Smartphone style={{ color: "white", width: "2rem", height: "2rem" }} />
              <h2 style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                color: "white",
                margin: 0,
                lineHeight: "1.4",
                textAlign: "center",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)"
              }}>
                סורקים ונכנסים לניהול חכם<br/>
                כל מה שצריך על הבניין במקום אחד
              </h2>
            </div>
            <div style={{
              ...tvStyles.cardContent,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: "1rem"
            }}>
              {/* QR Code */}
              <div style={{
                padding: "1rem",
                backgroundColor: "white",
                borderRadius: "0.75rem",
                border: "2px solid #e5e7eb",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
              }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent('https://bildiapp.com/')}`}
                  alt="QR Code for BildiApp"
                  style={{
                    width: "120px",
                    height: "120px",
                    display: "block"
                  }}
                />
              </div>

              {/* App Info */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.5rem"
              }}>
                <div style={{
                  background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)",
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: "0.5rem",
                  fontSize: "1.4rem",
                  fontWeight: "700",
                  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  letterSpacing: "0.5px"
                }}>
                  BILDIAPP
                </div>

                <p style={{
                  fontSize: "1.2rem",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "600"
                }}>
                  סרקו לגישה למערכת
                </p>

                <p style={{
                  fontSize: "1.1rem",
                  color: "#94a3b8",
                  margin: 0,
                  fontStyle: "italic"
                }}>
                  bildiapp.com
                </p>
              </div>
            </div>
          </div>
        </div>

        </div>
      </div>
    </>
  );
}